// Is a recipe vegetarian? Imported pages rarely say (schema.org suitableForDiet
// is uncommon), so it is guessed from the ingredient names, in English and
// Swedish, and a member can override the guess (recipes.vegetarian).
//
// Matched per word of the folded name: a word that is, starts with or ends with
// a keyword counts, so Swedish compounds work ("nötfärs", "kycklingbuljong",
// "tonfisk"). Words that only look like one are excepted ("färsk" is fresh,
// "beefsteak" a tomato), and a word that marks a plant-based stand-in
// ("vegofärs", "quorn mince", "vegetable stock") clears the whole ingredient.
// A wrong guess is one tap to fix, so the list errs towards the common words.

import type { Recipe, RecipeIngredient } from './supabase'
import { fold, tokens } from './normalize'

const MEAT_FISH = [
  // English
  'chicken', 'beef', 'pork', 'bacon', 'ham', 'lamb', 'mince', 'sausage', 'salami', 'chorizo',
  'prosciutto', 'pancetta', 'pepperoni', 'turkey', 'duck', 'veal', 'venison', 'steak', 'meat',
  'fish', 'salmon', 'tuna', 'cod', 'haddock', 'prawn', 'shrimp', 'crab', 'lobster', 'mussel',
  'clam', 'oyster', 'squid', 'anchov', 'sardine', 'mackerel', 'trout', 'gelatin', 'lard',
  'worcestershire',
  // Swedish (folded: å→a, ä→a, ö→o)
  'kyckling', 'kott', 'fars', 'flask', 'skinka', 'korv', 'lamm', 'kalv', 'biff', 'entrecote',
  'oxfile', 'kalkon', 'anka', 'hjort', 'lever', 'fisk', 'lax', 'torsk', 'sej', 'sill',
  'ansjovis', 'rakor', 'raka', 'mussl', 'krabba', 'hummer', 'kraft', 'blackfisk', 'gelatin',
]

// Words that start or end like a keyword but aren't meat or fish.
const NOT_MEAT = new Set([
  'farsk', 'farska', 'farskt', 'flaska', 'flaskor', 'beefsteak', 'hampa', 'graham', 'flax',
  'flaxseed', 'flaxseeds', 'kraftig', 'kraftigt', 'ankare', 'minced',
])

const PLANT_MARKERS = ['vego', 'vegan', 'vegetar', 'veggie', 'vegetable', 'quorn', 'soja', 'soy', 'tofu',
  'oumph', 'seitan', 'plant', 'vaxtbas']

const isMeatWord = (word: string) =>
  !NOT_MEAT.has(word) && MEAT_FISH.some(k => word.startsWith(k) || word.endsWith(k))

/** True when an ingredient name reads as meat, fish or seafood. */
export function isMeatOrFish(name: string): boolean {
  const words = tokens(fold(name).replace(/[^a-z0-9 ]/g, ' '))
  if (words.some(w => PLANT_MARKERS.some(m => w.startsWith(m)))) return false
  return words.some(isMeatWord)
}

export function looksVegetarian(ingredients: Pick<RecipeIngredient, 'name'>[]): boolean {
  return ingredients.length > 0 && !ingredients.some(g => isMeatOrFish(g.name))
}

/** A member's override wins; otherwise the guess. */
export function isVegetarian(r: Pick<Recipe, 'vegetarian' | 'ingredients'>): boolean {
  return r.vegetarian ?? looksVegetarian(r.ingredients)
}
